import { useEffect, useRef, useState } from 'react';
import { Text, textVariants } from '@etg/wings';
import { css, cx } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';
import { constants } from '@eti/utils';
import { AssetType, useAsset } from '@eti/components';

const DEFAULT_STEP_DURATION_MS = 5000;
const ANNOUNCEMENT_DURATION_MS = 5000;
const FIRST_ANNOUNCEMENT_MS = 100;

const LOADING_KEY = 'ScreenReader.PerceivedLoadingTimes.Loader.DefaultLabel1';
const STILL_LOADING_KEY = 'ScreenReader.PerceivedLoadingTimes.Loader.DefaultLabel2';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

const { MYTRIP, FLIGHTNETWORK, GOTOGATE } = constants.brands;

const BRAND_TO_FOLDER = {
  [FLIGHTNETWORK]: 'flightnetwork',
  [GOTOGATE]: 'gotogate',
  [MYTRIP]: 'mytrip',
} as const;

const hasABrandFolder = (brand: string = ''): brand is keyof typeof BRAND_TO_FOLDER =>
  Object.prototype.hasOwnProperty.call(BRAND_TO_FOLDER, brand);

const containerStyles = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-block: 100px;
`;

const animationStyles = css`
  height: 164px;
  width: 166px;
`;

const textStyles = css`
  max-width: 540px;
  text-align: center;
`;

const screenReadersOnlyStyles = css`
  border: 0;
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

interface LoaderProps {
  ariaLabelTextKeysArray?: string[];
  className?: string;
  dataTestId?: string;
  illustrationId: string;
  stepDurationMs?: number;
  textKeys?: string[];
}

const Loader = ({
  ariaLabelTextKeysArray = [LOADING_KEY, STILL_LOADING_KEY],
  className,
  dataTestId = 'loader',
  illustrationId,
  stepDurationMs = DEFAULT_STEP_DURATION_MS,
  textKeys = [],
}: LoaderProps) => {
  const { t } = useTranslation();
  const { brand: siteBrand } = useSiteContext();
  const brandCode = siteBrand.code;
  const brandFolder = hasABrandFolder(brandCode) ? BRAND_TO_FOLDER[brandCode] : 'default';
  const { ReactComponent: Animation } = useAsset(
    AssetType.Illustration,
    illustrationId,
    brandFolder,
  );
  const hasAnimation = Boolean(Animation);
  const animationRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;

  useEffect(() => {
    if (prefersReducedMotion) {
      const svg = animationRef.current?.querySelector('svg');
      if (svg && typeof svg.pauseAnimations === 'function') {
        svg.pauseAnimations();
      }
    }
  }, [prefersReducedMotion, Animation]);

  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = textKeys.length - 1;
  const [announcementIndex, setAnnouncementIndex] = useState<number>();
  const lastAnnouncementIndex = ariaLabelTextKeysArray.length - 1;
  const announcementKey =
    announcementIndex === undefined ? undefined : ariaLabelTextKeysArray[announcementIndex];

  // Set after mount, since a live region is only announced once its content changes.
  useEffect(() => {
    const firstAnnouncement = setTimeout(() => {
      setAnnouncementIndex(0);
    }, FIRST_ANNOUNCEMENT_MS);

    return () => {
      clearTimeout(firstAnnouncement);
    };
  }, []);

  // Counted per announcement, so each one is read out before the next replaces it.
  useEffect(() => {
    if (announcementIndex === undefined || announcementIndex >= lastAnnouncementIndex) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setAnnouncementIndex(announcementIndex + 1);
    }, ANNOUNCEMENT_DURATION_MS);

    return () => {
      clearTimeout(timeout);
    };
  }, [announcementIndex, lastAnnouncementIndex]);

  // Starts once the animation can be drawn, so the first text gets its full duration.
  useEffect(() => {
    if (!hasAnimation || activeIndex >= lastIndex) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setActiveIndex((current) => current + 1);
    }, stepDurationMs);

    return () => {
      clearTimeout(timeout);
    };
  }, [activeIndex, hasAnimation, lastIndex, stepDurationMs]);

  return (
    <div className={cx(containerStyles, className)} data-testid={dataTestId}>
      <div
        aria-atomic="true"
        aria-live="assertive"
        className={screenReadersOnlyStyles}
        data-testid="loaderAnnouncement"
        role="status"
      >
        {announcementKey && t(announcementKey)}
      </div>
      <div aria-hidden="true" className={animationStyles} ref={animationRef}>
        {Animation && <Animation data-testid="loaderAnimation" />}
      </div>
      {hasAnimation && textKeys.length > 0 && (
        <div className={textStyles} data-testid="loaderText">
          <Text as="p" variant={textVariants._16}>
            {t(textKeys[activeIndex])}
          </Text>
        </div>
      )}
    </div>
  );
};

export default Loader;
